const { ORIGIN, SITE_NAME, stripHtml, truncate } = require("./seo");
const { PRODUCT_ORIGIN, brandForDistrict } = require("./brand");
const { AREAS } = require("./areaPages");
const { propertyPath } = require("./propertySlug");
const { postPath } = require("./postSlug");
const {
    extractYouTubeId,
    youtubeWatchUrl,
    youtubeEmbedUrl,
    youtubeThumbnail,
    secondsToISO8601Duration,
} = require("./youtubeVideo");

/**
 * schema.org JSON-LD builders.
 *
 * GROUND RULE: everything here must be traceable to something the site or the
 * database already states. Structured data that misrepresents a business is
 * worse than none - it is the machine-readable version of a false claim, and
 * Google penalises it.
 *
 * Two things are therefore deliberately absent:
 *
 *   openingHours  - not published anywhere on the site, so there is nothing to
 *                   copy. Guessing "Mon-Fri 9-5" would be inventing a fact
 *                   about a real business.
 *   image on BlogPosting - none of the 19 posts has one. Without an image a
 *                   post is not eligible for an Article rich result, but
 *                   fabricating an image URL is not a fix.
 */

const ORG_ID = ORIGIN + "/#organization";

// Every value below appears in the site's own footer or contact page.
const OFFICE = {
    street: "204 Tooting High Street",
    locality: "London",
    postcode: "SW17 0SG",
    country: "GB",
    telephone: "+442089467171", // rendered as 020 8946 7171
    email: "info@property-people.co.uk",
    // The pin the contact page's own map drops on the office.
    lat: 51.4235821,
    lng: -0.1751521,
};

/*
 * Office hours, supplied by the business on 21 Aug 2026. They were absent
 * from the site entirely until then, which is why the schema below carried
 * no openingHours - guessing them would have been inventing a fact that
 * Google can surface directly in a local result, sending someone to a closed
 * office.
 *
 * One source for both the markup and the contact page. Structured data is
 * meant to describe what the page actually says, so hours that appear only
 * in JSON-LD and nowhere a visitor can read them would be the kind of
 * mismatch Google's guidelines warn about.
 *
 * Sunday is stated as closed rather than omitted: an absent day is
 * ambiguous, an explicit 00:00-00:00 is not.
 */
const HOURS = [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00", label: "Monday to Friday", display: "9am - 6pm" },
    { days: ["Saturday"], opens: "09:00", closes: "17:00", label: "Saturday", display: "9am - 5pm" },
    { days: ["Sunday"], closed: true, label: "Sunday", display: "Closed" },
];

/** The shape schema.org wants: one entry per block of days. */
function openingHours() {
    return HOURS.map(function (h) {
        return {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: h.days,
            opens: h.closed ? "00:00" : h.opens,
            closes: h.closed ? "00:00" : h.closes,
        };
    });
}

const SOCIAL = [
    "https://twitter.com/Property_People",
    "https://www.facebook.com/Property.People.London",
    "https://www.linkedin.com/company/property-people-london",
];

/** The business itself. Referenced by @id from the other blocks. */
function organisationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        "@id": ORG_ID,
        name: SITE_NAME,
        legalName: "Property People London Ltd",
        url: ORIGIN,
        telephone: OFFICE.telephone,
        email: OFFICE.email,
        image: ORIGIN + "/images/pp-outside-dark.jpg",
        logo: ORIGIN + "/favicon.png",
        // "Established 2010" appears in the site header.
        foundingDate: "2010",
        address: {
            "@type": "PostalAddress",
            streetAddress: OFFICE.street,
            addressLocality: OFFICE.locality,
            postalCode: OFFICE.postcode,
            addressCountry: OFFICE.country,
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: OFFICE.lat,
            longitude: OFFICE.lng,
        },
        openingHoursSpecification: openingHours(),
        areaServed: AREAS.map((a) => ({ "@type": "Place", name: a.name })),
        sameAs: SOCIAL,
    };
}

/** UK postcode out of a stored address like "…, London SW17 0HL, UK". */
function extractPostcode(address) {
    if (!address) return null;
    const m = String(address).match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/i);
    return m ? m[1].toUpperCase().replace(/\s+/g, " ") : null;
}

/** schema.org type for the stored free-text property type. */
function residenceType(type) {
    const t = String(type || "").toLowerCase();
    if (/(flat|apartment|maisonette|studio)/.test(t)) return "Apartment";
    if (/(house|terrace|detached|bungalow)/.test(t)) return "House";
    return "Residence";
}

function availability(status) {
    if (status === "Available") return "https://schema.org/InStock";
    if (status === "Under Offer") return "https://schema.org/LimitedAvailability";
    return "https://schema.org/SoldOut"; // Let, Sold
}

/*
 * `origin` defaults to the agency, so every existing caller is unchanged. The
 * house price pages pass the product origin instead: a breadcrumb that walks
 * back to property-people.co.uk from a page served at doorstepdata.co.uk is a
 * trail to a page that is not its parent.
 */
function breadcrumb(trail, origin) {
    const base = origin || ORIGIN;
    return {
        "@type": "BreadcrumbList",
        itemListElement: trail.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            item: base + item.path,
        })),
    };
}

/**
 * One property listing.
 *
 * `offers` differs by category because a rent and a sale price are not the
 * same kind of number. A letting is a recurring monthly or weekly amount, so
 * it uses a UnitPriceSpecification; a sale is a single figure.
 */
function propertySchema(property) {
    if (!property || !property._id) return null;

    const url = ORIGIN + propertyPath(property);
    const isLetting = property.category !== "Sales";
    const where = [property.displayAddress, property.location].filter(Boolean).join(", ");
    const postcode = extractPostcode(property.address);

    // Built by assignment rather than as a literal with `|| undefined`
    // placeholders: JSON.stringify drops undefined values, but leaving them in
    // the object makes it impossible to tell a genuinely absent field from a
    // typo when inspecting the output.
    const postalAddress = {
        "@type": "PostalAddress",
        addressLocality: property.location || "London",
        addressRegion: "London",
        addressCountry: "GB",
    };
    if (property.displayAddress) postalAddress.streetAddress = property.displayAddress;
    if (postcode) postalAddress.postalCode = postcode;

    const residence = {
        "@type": residenceType(property.type),
        name: where,
        address: postalAddress,
    };

    if (property.bedrooms) residence.numberOfBedrooms = property.bedrooms;
    if (property.bathrooms) residence.numberOfBathroomsTotal = property.bathrooms;
    if (property.coordinates && typeof property.coordinates.lat === "number") {
        residence.geo = {
            "@type": "GeoCoordinates",
            latitude: property.coordinates.lat,
            longitude: property.coordinates.lng,
        };
    }

    let offers;
    if (isLetting) {
        offers = {
            "@type": "Offer",
            availability: availability(property.status),
            businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: property.price,
                priceCurrency: "GBP",
                unitText: property.priceType === "PW" ? "WEEK" : "MONTH",
            },
        };
    } else {
        offers = {
            "@type": "Offer",
            availability: availability(property.status),
            businessFunction: "http://purl.org/goodrelations/v1#Sell",
            price: property.price,
            priceCurrency: "GBP",
        };
    }

    const description = stripHtml(property.summary) || stripHtml(property.description);

    const listing = {
        "@type": "RealEstateListing",
        "@id": url,
        url: url,
        name: where,
        about: residence,
        offers: offers,
        provider: { "@id": ORG_ID },
    };

    if (description) listing.description = truncate(description, 300);
    if (property.photos && property.photos.length) listing.image = property.photos.slice(0, 6);
    if (property.createdAt) listing.datePosted = new Date(property.createdAt).toISOString();

    // If this property's locality has a landing page, that page is its real
    // parent in the hierarchy - which also reinforces the internal link.
    const parentArea = AREAS.find(
        (a) => property.location && a.locations.indexOf(property.location) !== -1
    );

    const trail = [{ name: "Home", path: "/" }];
    if (parentArea) trail.push({ name: parentArea.name, path: "/estate-agents/" + parentArea.slug });
    trail.push({ name: where, path: propertyPath(property) });

    return {
        "@context": "https://schema.org",
        "@graph": [listing, breadcrumb(trail)],
    };
}

/**
 * One blog post. An image is emitted only when the post actually has one:
 * Google requires an image for an Article rich result, but an image property
 * pointing at the generic office photograph would claim every post is
 * illustrated by the same picture, which is worse than omitting it.
 */
/**
 * A post's YouTube video, if it has one. Only videoUrl is required for this
 * to appear at all; uploadDate and duration are each included only when
 * api/youtubeMeta.js managed to resolve them at save time - see the note
 * there on why a private or unreachable video degrades to a smaller, still
 * valid VideoObject rather than a missing one.
 */
function videoSchema(post, url, description) {
    const id = extractYouTubeId(post.videoUrl);
    if (!id) return null;

    const video = {
        "@type": "VideoObject",
        "@id": url + "#video",
        name: truncate(post.title || SITE_NAME, 110),
        description: description || post.title || SITE_NAME,
        thumbnailUrl: youtubeThumbnail(id),
        contentUrl: youtubeWatchUrl(id),
        embedUrl: youtubeEmbedUrl(id),
    };

    if (post.videoUploadDate) video.uploadDate = post.videoUploadDate;
    const duration = secondsToISO8601Duration(post.videoDurationSeconds);
    if (duration) video.duration = duration;

    return video;
}

function postSchema(post) {
    if (!post || !post._id) return null;

    const url = ORIGIN + postPath(post);
    const body = stripHtml(post.excerpt) || stripHtml(post.content);
    const description = body ? truncate(body, 300) : null;

    // Posts are authored under the company name rather than an individual, so
    // Organization is the accurate type.
    const authorName = post.username || SITE_NAME;
    const author =
        authorName === SITE_NAME
            ? { "@type": "Organization", name: SITE_NAME, "@id": ORG_ID }
            : { "@type": "Person", name: authorName };

    const posting = {
        "@type": "BlogPosting",
        "@id": url,
        headline: truncate(post.title || SITE_NAME, 110),
        url: url,
        mainEntityOfPage: url,
        author: author,
        publisher: { "@id": ORG_ID },
    };

    if (post.image) posting.image = post.image;
    if (description) posting.description = description;
    if (post.createdAt) posting.datePublished = new Date(post.createdAt).toISOString();
    if (post.updatedAt) posting.dateModified = new Date(post.updatedAt).toISOString();

    const graph = [
        posting,
        breadcrumb([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title || "Post", path: postPath(post) },
        ]),
    ];

    // Referenced from the posting rather than nested inline, the same way
    // publisher points at the Organization entry above - a separate @graph
    // entry is what lets Google surface the video in its own right (the
    // Video tab, a video carousel) rather than only as part of the article.
    const video = post.videoUrl ? videoSchema(post, url, description) : null;
    if (video) {
        posting.video = { "@id": video["@id"] };
        graph.push(video);
    }

    return {
        "@context": "https://schema.org",
        "@graph": graph,
    };
}

/** An area landing page: a collection of listings, plus the trail to it. */
function localitySchema(area, stats) {
    if (!area) return null;

    const url = ORIGIN + "/estate-agents/" + area.slug;
    const s = stats || {};
    const service = s.lettingsLed === false ? "Estate Agents" : "Letting Agents";

    const page = {
        "@type": "CollectionPage",
        "@id": url,
        url: url,
        name: service + " in " + area.name,
        about: { "@type": "Place", name: area.name },
        provider: { "@id": ORG_ID },
    };

    if (s.total) {
        page.description =
            "Property People have let or sold " + s.total + " properties in " + area.name + ".";
    }

    // Two crumbs, not three: there is no areas index page, and pointing a
    // middle crumb at this same URL would just be a duplicate.
    return {
        "@context": "https://schema.org",
        "@graph": [
            page,
            breadcrumb([
                { name: "Home", path: "/" },
                { name: area.name, path: "/estate-agents/" + area.slug },
            ]),
        ],
    };
}

/**
 * A house price district page.
 *
 * WebPage rather than Dataset, which was the first instinct. Dataset is for a
 * dataset somebody can fetch and use; this is a page of statistics derived
 * from one. Marking it up as a Dataset would be claiming a download that does
 * not exist, and the markup you can defend is worth more than the markup with
 * the prettier rich result.
 *
 * `about` is the district as a Place, which is what the page is actually
 * about, and the Land Registry attribution rides along in `isBasedOn` so the
 * provenance the page states in words is also stated in the markup.
 */
function housePricesSchema(district, stats) {
    if (!district) return null;

    // The owning brand's domain, not the product's unconditionally - the five
    // South West London areas are the agency's. A schema @id on one hostname
    // describing a page served from another is a contradiction a search engine
    // resolves by ignoring the markup.
    const url = brandForDistrict(district).origin + "/house-prices/" + district.toLowerCase();
    const s = stats || {};

    const page = {
        "@type": "WebPage",
        "@id": url,
        url: url,
        name: "House prices in " + district,
        about: {
            "@type": "Place",
            name: district + (s.authority ? ", " + s.authority : ""),
            address: { "@type": "PostalAddress", postalCode: district, addressCountry: "GB" },
        },
        /* No provider: ORG_ID is a node on property-people.co.uk and does not
           resolve from a page served at doorstepdata.co.uk. Better absent
           than dangling. */
        /* CreativeWork, NOT Dataset. A Dataset node anywhere in the markup -
           nested or not - is validated by Google as a dataset THIS site
           publishes, and demands a description and creator for it. It is
           HM Land Registry's dataset, not ours (see above), so the source is
           described as the published work it is, credited to its creator.
           Search Console flagged the Dataset version on 6 Oct 2026. */
        isBasedOn: {
            "@type": "CreativeWork",
            name: "HM Land Registry Price Paid Data",
            url: "https://www.gov.uk/government/statistical-data-sets/price-paid-data-downloads",
            license: "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/",
            creator: {
                "@type": "Organization",
                name: "HM Land Registry",
                url: "https://www.gov.uk/government/organisations/land-registry",
            },
        },
    };

    if (s.median && s.total) {
        page.description =
            "The median home in " +
            district +
            " sold for £" +
            Math.round(s.median).toLocaleString("en-GB") +
            " over the last " +
            s.years +
            " years, across " +
            s.total.toLocaleString("en-GB") +
            " sales.";
    }

    return {
        "@context": "https://schema.org",
        "@graph": [
            page,
            breadcrumb(
                [
                    { name: "Home", path: "/" },
                    { name: "House prices", path: "/house-prices" },
                    { name: district, path: "/house-prices/" + district.toLowerCase() },
                ],
                // The owning brand's, matching `url` above. A trail climbing
                // from an agency district page to doorstepdata.co.uk names a
                // parent that is not this page's parent.
                brandForDistrict(district).origin
            ),
        ],
    };
}

/*
 * The index of district pages.
 *
 * BOTH BRANDS HAVE ONE NOW, each listing only the districts it owns, so the
 * origin is a parameter rather than a constant. Passing the product's by
 * default keeps every existing caller correct, because the product's index is
 * the larger one and was the only one that existed before.
 *
 * The districts handed in must already be filtered to this brand. Listing a
 * district the other domain owns would publish a link that canonicalises
 * elsewhere, which is the cross-brand leak this whole split exists to stop.
 */
function housePricesIndexSchema(districts, origin) {
    const url = (origin || PRODUCT_ORIGIN) + "/house-prices";
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "CollectionPage",
                "@id": url,
                url: url,
                name: "House prices by postcode district",
                hasPart: (districts || []).map((d) => ({
                    "@type": "WebPage",
                    // Per district, not per index. These agree for a correctly
                    // filtered list; deriving it rather than reusing `url`
                    // means a list that somehow carries a foreign district
                    // links to where that page actually is, instead of
                    // claiming it for this domain.
                    url: brandForDistrict(d).origin + "/house-prices/" + d.toLowerCase(),
                    name: "House prices in " + d,
                })),
            },
            breadcrumb(
                [
                    { name: "Home", path: "/" },
                    { name: "House prices", path: "/house-prices" },
                ],
                // A trail walking back to the other brand's home page points
                // at something that is not this page's parent.
                origin || PRODUCT_ORIGIN
            ),
        ],
    };
}

/**
 * The report tool.
 *
 * WebApplication with a zero-price offer, because that is exactly what it is:
 * a thing you use in a browser, at no cost, with no account. The free part is
 * the whole proposition against the paid report sites, so it is worth stating
 * in the markup and not only in the copy.
 */
function reportToolSchema() {
    const url = ORIGIN + "/property-report";
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebApplication",
                "@id": url,
                url: url,
                name: "Free Property Report",
                applicationCategory: "BusinessApplication",
                browserRequirements: "Requires JavaScript.",
                operatingSystem: "Any",
                provider: { "@id": ORG_ID },
                offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
                featureList: [
                    "Council tax bands and charges",
                    "Energy performance certificate",
                    "Sold prices and comparable sales",
                    "Monthly running costs",
                    "Broadband availability",
                    "Schools, shops, parks, stations and buses",
                    "Crime, planning designations and flood risk",
                ],
            },
            breadcrumb([
                { name: "Home", path: "/" },
                { name: "Property report", path: "/property-report" },
            ]),
        ],
    };
}

/**
 * The product's homepage.
 *
 * WebSite with a SearchAction, because that is what the page is: a search box
 * over a dataset. The target is the tool with the postcode it was given - the
 * same hand-off the homepage form makes - so the markup describes a journey
 * that actually works rather than one invented for the schema.
 */
function productHomeSchema() {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": PRODUCT_ORIGIN + "/#website",
                url: PRODUCT_ORIGIN + "/",
                name: "Doorstep Data",
                description:
                    "Public record data for one address in England and Wales, from government sources.",
                inLanguage: "en-GB",
                potentialAction: {
                    "@type": "SearchAction",
                    target: {
                        "@type": "EntryPoint",
                        urlTemplate: PRODUCT_ORIGIN + "/property-report?postcode={search_term_string}",
                    },
                    "query-input": "required name=search_term_string",
                },
            },
        ],
    };
}

/*
 * CommonJS on purpose: api/render.js requires this directly so the HTML the
 * server sends and the tags React sets in the browser come from one
 * implementation. Two copies would drift, and a page whose served title
 * differs from its rendered title sends a search engine mixed signals.
 * webpack resolves the named imports elsewhere in the app unchanged.
 */
module.exports = {
    HOURS,
    OFFICE,
    organisationSchema,
    propertySchema,
    postSchema,
    localitySchema,
    housePricesSchema,
    housePricesIndexSchema,
    productHomeSchema,
    reportToolSchema,
};
